<template>
  <!-- flex bags -->
  <content-section
    icon="luggage"
    title="Bagagli in stiva extra"
    :description="
      Object.keys(bags).length > 0
        ? `Scegli i bagagli al checkin per passeggero e per itinierario.`
        : `Non è possibile aggiungere bagagli extra su questo volo. Normalmente
            è possibile farlo sul sito della compagnia aerea dopo avere
            effettuato la prenotazione. E' anche possibile che in questa pagina,
            nella sezione Tariffe, ti vengano proposte delle Tariffe che
            includono bagagli a mano o in stiva. Controlla.`
    "
    :classes="'bags'"
  >
    <template v-if="Object.keys(bags).length > 0">
      <div class="overflow-auto">
        <table class="table">
          <thead>
            <tr>
              <th></th>
              <th></th>
              <th
                v-for="(itinerary, itinerary_index) in flight_offer.itineraries"
                :key="`th-${itinerary_index}`"
              >
                {{ itinerary_header(itinerary, itinerary_index) }}
              </th>
            </tr>
          </thead>
          <tbody
            v-for="(traveler, traveler_index) in flexible_bags"
            v-bind:key="traveler_index + _uid"
          >
            <tr>
              <td class="py-2 px-1 bags--traveler" rowspan="999999">
                <!-- inutile creare un metodo solo per calcolare questo valore -->
                <span class="semibold">Viaggiatore {{ traveler_details[traveler_index].id }}</span>
                ({{
                  transcode_traveler_type(
                    traveler_details[traveler_index].traveler_type
                  )
                }})
              </td>
            </tr>
            <tr
              v-for="(bag, bag_index) in bags"
              v-bind:key="traveler_index + bag_index + _uid"
            >
              <template v-if="bag.quantity < 3">
                <td class="py-2 px-0">
                  <div>
                    <span class="semibold">
                      {{ bag.quantity ? bag.quantity : "1" }}
                      {{ bag.quantity > 1 ? "bagagli" : "bagaglio" }}
                    </span>
                    <span v-if="bag.weight">
                      ({{ bag.weight }} {{ bag.weightUnit }} totali)</span
                    >
                    +{{ bag_price(bag) }}
                    {{ bag.price.currencyCode }}/itinerario
                  </div>

                  <div
                    class="badge-baggage mt-2"
                    v-b-popover.hover.top="
                      bag.bookableByItinerary
                        ? badge_popover_unlock
                        : badge_popover_lock
                    "
                  >
                    <b-badge variant="light" class="py-1">
                      <vb-icon
                        v-if="bag.bookableByItinerary"
                        name="unlock"
                        :size="18"
                        class="badge--icon"
                      >
                      </vb-icon>
                      <vb-icon v-else name="lock" :size="18" class="badge--icon">
                      </vb-icon>
                      <span class="badge--text">{{
                        bag.bookableByItinerary
                          ? "Sui singoli itinerari"
                          : "Su tutti gli itinerari"
                      }}</span>
                    </b-badge>
                  </div>
                </td>
                <td
                  class="py-2 px-0 text-center"
                  v-for="(
                    itinerary, itinerary_index
                  ) in flight_offer.itineraries"
                  v-bind:key="itinerary_index"
                  :set="
                    available =
                      itinerary.segments.filter((s) => {
                        return bag.segmentIds.includes(s.id);
                      }).length > 0
                  "
                >
                  <!--<b-badge v-if="!available" variant="gray">non disponibile</b-badge>-->
                  <vb-checkbox
                    v-if="available"
                    :key="flexible_bags_update_key"
                    @change="
                      (value) => {
                        flexible_bags_updated(value, {
                          traveler_index: number(traveler_index),
                          itinerary_index: number(itinerary_index),
                          bag_index: number(bag_index),
                          bookableByItinerary: bag.bookableByItinerary,
                        });
                      }
                    "
                    :vb_options="{
                      value:
                        flexible_bags[traveler_index][itinerary_index][
                          bag_index
                        ].added,
                    }"
                  />
                </td>
              </template>
            </tr>
          </tbody>
        </table>
        <div class="text-right mr-md-5">
          <vb-button
            :vb_options="{
              variant: 'info',
              size: 'md',
              disabled: !has_pending_bag_changes,
            }"
            @click="
              handle_add_flexible_bag_to_flight_offer(
                flight_offer,
                flexible_bags
              )
            "
          >
            Aggiorna
          </vb-button>
        </div>
      </div>
    </template>
  </content-section>
</template>

<script>
import { divide, number } from "mathjs";
import {
  transcode_traveler_type,
  apply_markup_to_number,
} from "@liveflight/utilities/flight-offer"; 
import ContentSection from "@liveflight/components/blocks/wrappers/content-section";
import { get_trip_type_from_itineraries, TRIP_TYPES } from "@liveflight/utilities/origin-destination";

export default {
  name: "additional-bags-table",
  props: {
    traveler_details: Array,

    flexible_bags: Object,
    bags: Object,

    flexible_bags_update_key: Number,
    has_pending_bag_changes: Boolean,
    flight_offer: Object,

    handle_add_flexible_bag_to_flight_offer: Function,
    flexible_bags_updated: Function,
  },
  components: {
    "content-section": ContentSection,
  },
  computed: {
    trip_type() {
      return get_trip_type_from_itineraries(this.flight_offer.itineraries);
    },
  },
  data() {
    return { 
      badge_popover_lock:
        "Il bagaglio extra non è acquistabile per un singolo itinerario ma solo per tutto il viaggio.",
      badge_popover_unlock:
        "Puoi acquistare il bagaglio extra anche per una sola parte del viaggio.",
    };
  },
  methods: {
    number,
    divide,
    apply_markup_to_number,
    transcode_traveler_type,
    itinerary_header(itinerary, itinerary_index) {
      if (this.trip_type === TRIP_TYPES.ONE_WAY || this.trip_type === TRIP_TYPES.ROUND_TRIP) {
        const labels = ["Andata", "Ritorno"];
        return labels[itinerary_index] || "";
      }
      const { segments = [] } = itinerary;
      if (segments.length === 0) {
        return `Tratta ${itinerary_index + 1}`;
      }
      const { departure } = segments[0];
      const { arrival } = segments[segments.length - 1];
      return `${departure.iataCode} → ${arrival.iataCode}`;
    },
    bag_price(bag) {
      const bag_live_price = this.apply_markup_to_number(
        this.flight_offer,
        bag.price.amount
      );
      const itineraries_count = (this.flight_offer.itineraries || []).length;
      if (bag.bookableByItinerary || itineraries_count !== 0) {
        return bag_live_price;
      }
      return this.divide(bag_live_price, itineraries_count);
    },
  },
};
</script>

<style lang="scss" scoped>
.bags {
  overflow: hidden;
  .table {
    thead {
      background-color: #f8f9fa;

      th {
        border-bottom: 0;
        text-align: center;
      }
    }
    tbody {
      border-bottom: 1px solid $gray-300;

      &:before {
        content: "";
        display: block;
        height: 10px;
      }

      & + tbody {
        border: 0;
      }
    }

    td {
      border-width: 0 0 1px;
      border-style: solid;
      border-color: $gray-300;
    }
  }
  &--traveler {
    max-width: 100px;
  }
}
</style>
